/**
 * Thin wrapper around the Cloudflare Zaraz Consent API.
 *
 * Zaraz is the system of record for consent on adventive.com: it gates the
 * tools it loads (GA4, HubSpot) and writes the `cf_consent` cookie. This module
 * only reads that state and writes the visitor's choices back through the
 * documented API (https://developers.cloudflare.com/zaraz/consent-management/api/).
 *
 * The branded UI (components/CookieConsent.tsx) and the ad loaders
 * (lib/ad-url.ts) both depend on this file and nothing else.
 */

export interface ZarazPurpose {
  id?: string;
  name: string | Record<string, string>;
  description?: string | Record<string, string>;
  order?: number;
}

interface ZarazConsentApi {
  APIReady?: boolean;
  modal?: boolean;
  purposes?: Record<string, ZarazPurpose>;
  get(id: string): boolean | undefined;
  getAll(): Record<string, boolean>;
  set(prefs: Record<string, boolean>): void;
  setAll(value: boolean): void;
  sendQueuedEvents(): void;
}

declare global {
  interface Window {
    zaraz?: { consent?: ZarazConsentApi };
    AdventiveConsent?: {
      open: () => void;
      openBanner: () => void;
      close: () => void;
      granted: (purposeName: string) => boolean | undefined;
    };
  }
}

/** Purpose names as configured in Zaraz → Consent → Purposes (case-insensitive). */
export const PURPOSES = {
  analytics: 'Analytics',
  marketing: 'Marketing',
  adDemo: 'Advertising Demonstration',
} as const;

/** Zaraz purpose names/descriptions are either a string or a `{ [lang]: string }` map. */
export function localized(value: string | Record<string, string> | undefined, lang = 'en'): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.en || Object.values(value)[0] || '';
}

function api(): ZarazConsentApi | undefined {
  return typeof window !== 'undefined' ? window.zaraz?.consent : undefined;
}

/** True once Zaraz's Consent API is usable on this page. */
export function isConsentReady(): boolean {
  return !!api()?.APIReady;
}

/**
 * Resolves when the Consent API is ready, or after `timeoutMs` if Zaraz never
 * loads (blocked by an extension, dev server without Cloudflare, etc.).
 * Resolves to `true` if the API is available, `false` on timeout.
 */
export function whenConsentReady(timeoutMs = 3000): Promise<boolean> {
  if (isConsentReady()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      document.removeEventListener('zarazConsentAPIReady', onReady);
      resolve(ok);
    };
    const onReady = () => finish(true);
    document.addEventListener('zarazConsentAPIReady', onReady);
    window.setTimeout(() => finish(isConsentReady()), timeoutMs);
  });
}

/** All configured purposes, sorted by their Zaraz order, with ids attached. */
export function getPurposes(): Array<ZarazPurpose & { id: string }> {
  const ps = api()?.purposes || {};
  return Object.keys(ps)
    .map((id) => ({ ...ps[id], id }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function findPurposeId(purposeName: string): string | undefined {
  const target = purposeName.trim().toLowerCase();
  return getPurposes().find((p) => localized(p.name).trim().toLowerCase() === target)?.id;
}

/**
 * Consent status for a purpose by its display name.
 * `undefined` = API not ready or purpose not configured.
 */
export function isGranted(purposeName: string): boolean | undefined {
  const a = api();
  if (!a?.APIReady) return undefined;
  const id = findPurposeId(purposeName);
  if (!id) return undefined;
  return a.getAll()[id] === true;
}

export function getAllConsent(): Record<string, boolean> {
  return api()?.getAll() || {};
}

export function setConsent(prefs: Record<string, boolean>): void {
  const a = api();
  if (!a) return;
  a.set(prefs);
  a.sendQueuedEvents();
}

export function setAllConsent(value: boolean): void {
  const a = api();
  if (!a) return;
  a.setAll(value);
  a.sendQueuedEvents();
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(cb: () => void): () => void {
  document.addEventListener('zarazConsentChoicesUpdated', cb);
  document.addEventListener('zarazConsentAPIReady', cb);
  return () => {
    document.removeEventListener('zarazConsentChoicesUpdated', cb);
    document.removeEventListener('zarazConsentAPIReady', cb);
  };
}

/**
 * The GDPR signal to put on ad-server requests for THIS visitor, expressed the
 * way a TCF CMP would: `gdpr=0` when storage is allowed (consent granted, or
 * visitor outside the consent regions where the Zaraz region tool auto-grants),
 * otherwise `gdpr=1&gdpr_consent=` (GDPR applies, no consent string → the ad
 * server must not store identifiers). Ads render either way.
 */
export function adGdprSignal(): string {
  return isGranted(PURPOSES.adDemo) === true ? 'gdpr=0' : 'gdpr=1&gdpr_consent=';
}

/* ---------- UI bridge (used by the footer link and the Zaraz region-check tool) ---------- */

type Listener = (view: 'banner' | 'manage' | 'closed') => void;
const listeners = new Set<Listener>();

export function subscribeConsentUI(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function openCookieSettings(): void {
  listeners.forEach((l) => l('manage'));
}
export function openCookieBanner(): void {
  listeners.forEach((l) => l('banner'));
}
export function closeCookieUI(): void {
  listeners.forEach((l) => l('closed'));
}
